import React, { useState } from 'react';
import { FiSend } from 'react-icons/fi';
import { unsubscribe } from '../api';
import { showToast } from '../toaster';

const reasons = [
  'I do not understand the amount on my bank statement',
  'I wish to receive the invoice of my order',
  'I wish to cancel',
  'I want to unsubscribe',
  'Other',
];

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

const isValidIbanLastFive = (value: string) =>
  /^\d{5}$/.test(value.trim());

export default function ContactForm() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [iban, setIban] = useState('');
  const [reasonIdx, setReasonIdx] = useState(0);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setIban('');
    setReasonIdx(0);
    setMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedIban = iban.trim();

    if (!isValidEmail(trimmedEmail)) {
      showToast('Please enter a valid email address.', 'error');
      return;
    }

    if (!isValidIbanLastFive(trimmedIban)) {
      showToast('Please enter exactly the last 5 digits of your IBAN.', 'error');
      return;
    }

    setLoading(true);
    const isUnsub = reasonIdx === 2 || reasonIdx === 3;
    try {
      await unsubscribe({
        first_name: firstName,
        last_name: lastName,
        email: trimmedEmail,
        iban_last_5: trimmedIban,
        is_unsubscribe: isUnsub,
        message,
        options: { reason: reasons[reasonIdx] },
      });
      showToast(
        isUnsub ? 'Unsubscribe request sent successfully.' : 'Message sent successfully.',
        'success'
      );
      reset();
    } catch (err: any) {
      let msg = err?.message || 'Something went wrong.';
      // If error is JSON, try to extract message
      try {
        const parsed = JSON.parse(msg);
        if (parsed && typeof parsed.message === 'string') msg = parsed.message;
      } catch {}
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6 flex flex-col gap-3 border-b border-emerald-900/10 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#0D6D65]">Request form</p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-[#0B2F2A]">Contact us</h2>
        </div>
        <p className="max-w-sm text-sm leading-6 text-[#2D4A43]">Our operators will answer your questions quickly and efficiently.</p>
      </div>
      <form className="space-y-5 text-sm" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="first-name" className="sr-only">First name *</label>
            <input
              id="first-name"
              name="first_name"
              aria-label="First name"
              placeholder="First name *"
              className="w-full rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] placeholder:text-[#526D66] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="last-name" className="sr-only">Last name *</label>
            <input
              id="last-name"
              name="last_name"
              aria-label="Last name"
              placeholder="Last name *"
              className="w-full rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] placeholder:text-[#526D66] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              required
            />
          </div>
        </div>
        <div>
          <label htmlFor="email" className="sr-only">Email address *</label>
          <input
            id="email"
            name="email"
            type="email"
            aria-label="Email address"
            placeholder="Email *"
            className="w-full rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] placeholder:text-[#526D66] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>
        <div>
          <label htmlFor="iban" className="sr-only">Last 5 digits of IBAN *</label>
          <input
            id="iban"
            name="iban"
            aria-label="Last 5 digits of IBAN"
            placeholder="Last 5 digits of IBAN *"
            className="w-full rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] placeholder:text-[#526D66] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
            value={iban}
            onChange={e => setIban(e.target.value.replace(/\D/g, '').slice(0, 5))}
            inputMode="numeric"
            pattern="[0-9]{5}"
            title="Enter exactly the last 5 digits of your IBAN"
            required
            maxLength={5}
          />
        </div>
        <div>
          <label htmlFor="reason" className="sr-only">Select reason for request</label>
          <select
            id="reason"
            name="reason"
            aria-label="Select reason for request"
            className="w-full rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
            value={reasonIdx}
            onChange={e => setReasonIdx(Number(e.target.value))}
          >
            {reasons.map((r, i) => (
              <option value={i} key={r}>{`${i + 1} - ${r}`}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="message" className="sr-only">Your message *</label>
          <textarea
            id="message"
            name="message"
            aria-label="Your message"
            placeholder="Your message *"
            className="h-32 w-full resize-none rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] placeholder:text-[#526D66] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
            value={message}
            onChange={e => setMessage(e.target.value)}
            required
          />
        </div>
        <button
          type="submit"
          aria-label="Send message"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#D97706] py-4 text-base font-semibold text-white shadow-lg shadow-amber-900/15 transition-colors hover:bg-[#B45309] disabled:opacity-60"
          disabled={loading}
        >
          <FiSend className="w-4 h-4" /> {loading ? 'SENDING...' : 'SEND MESSAGE'}
        </button>
      </form>
    </div>
  );
}
